import React, { useState, useMemo, useEffect } from 'react';
import { Drawer, Segmented, Table, Select, Button, ConfigProvider, message, Spin } from 'antd';
import { LockOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { Driver } from '../types/DeliveryTaskType';
import { GET_DELIVERY_TASK_ORDER_BY_TASK_ID, DeliveryTaskOrderDetail } from '../graphql/queries/getDeliveryTaskOrderByTaskId';
import { TRANSFER_PARCELS, TransferParcelsInput } from '../graphql/mutations/transferParcels';
import { GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID } from '../graphql/queries/getAvailableDriversByWarehouseId';
import { GET_AVAILABLE_DRIVERS } from '../graphql/queries/getAvailableDrivers';
import { WarehouseContext } from '../../../../contexts/WarehouseContext';
import { useContext } from 'react';

import { t } from '../../../../i18n';
import dayjs from 'dayjs';

interface AllocateParcelsDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: (transferTaskId?: number) => void; // ✅ 传递 transferTaskId，用于轮询
  taskId?: string;
  currentDriverId?: number;
  taskDspId?: number; // 任务的DSP ID，从列表数据中获取，避免额外查询
  isDriverVerificationEnabled?: boolean; // 司机实名验证开关（全量开启）
  mode?: 'wms' | 'dsp';
}


interface ZipcodeGroup {
  zipcode: string;
  count: number;
  parcels: DeliveryTaskOrderDetail[];
}

const AllocateParcelsDrawer: React.FC<AllocateParcelsDrawerProps> = ({
  visible,
  onClose,
  onSuccess,
  taskId,
  currentDriverId,
  taskDspId,
  isDriverVerificationEnabled = true, // 默认全量开启
  mode,
}) => {
  const [activeTab, setActiveTab] = useState<'shipment' | 'zipcode'>('shipment');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  
  // ✅ 单个司机分配（Shipment模式）
  const [selectedParcels, setSelectedParcels] = useState<React.Key[]>([]);
  
  // ✅ 单个司机分配（Zipcode模式）
  const [selectedZipcodes, setSelectedZipcodes] = useState<string[]>([]);
  
  // ✅ 司机信息（通用，两种模式共用）
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>(undefined);
  
  const [expandedZipcodes, setExpandedZipcodes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const { data, loading: orderDetailsLoading, refetch } = useQuery(GET_DELIVERY_TASK_ORDER_BY_TASK_ID, {
    variables: { deliveryTaskId: taskId ? parseInt(taskId) : null },
    skip: !taskId,
    fetchPolicy: 'network-only'  // ✅ 每次都从网络获取最新数据，不使用缓存
  });

  // 可转单包裹：后端已过滤（已妥投、已转出、终态），直接使用
  const orderDetails: DeliveryTaskOrderDetail[] = data?.getDeliveryTaskOrderByTaskId || [];

  // 获取可用司机列表（排除当前任务的司机，避免自己转给自己）
  // 策略：
  // - DSP 经理：只显示自己 DSP 的司机（使用 getAvailableDrivers，后端从角色自动获取 dspId）
  // - WMS 用户：显示该任务对应dsp下的司机（12.29 不允许跨 DSP 转单） 
  // 使用 WarehouseContext（WMS 和 Delivery 布局都通过 WarehouseProvider 提供）
  const warehouseContext = useContext(WarehouseContext);
  const warehouse = warehouseContext?.currentWarehouse || null;
  const isDsp = mode === 'dsp';
  
  // 统一提取 warehouseId：支持 number 和 string 类型
  const warehouseId = React.useMemo(() => {
    if (!warehouse?.id) return undefined;
    // WarehouseContext 返回的是 string，需要转换为 number
    return typeof warehouse.id === 'string' ? warehouse.id : String(warehouse.id);
  }, [warehouse?.id]);
  
  // WMS 用户：使用 warehouseId 查询，如果任务有dspId则传入dspId进行过滤
  const { data: driversDataByWarehouse, loading: driversLoadingByWarehouse, error: driversErrorByWarehouse } = useQuery(GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID, {
    variables: { 
      warehouseId: warehouseId ? Number(warehouseId) : undefined,
      dspId: taskDspId || null  // 根据任务的dspId过滤，确保只显示该DSP的司机
    },
    skip: !warehouseId || isDsp, // DSP 经理跳过此查询
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  // DSP 经理：使用 getAvailableDrivers（后端自动从角色获取 dspId）
  const { data: driversDataByDsp } = useQuery(GET_AVAILABLE_DRIVERS, {
    skip: !isDsp // 只有 DSP 经理才使用
  });

  // 根据角色选择司机数据源
  const drivers: Driver[] = isDsp
    ? (driversDataByDsp?.getAvailableDrivers || [])
    : (driversDataByWarehouse?.getAvailableDriversByWarehouseId || []);
  
  // 兜底 确保只显示任务DSP的司机，并过滤离职司机
  const activeDrivers = drivers.filter(driver => {
    const isActive = driver.status === 1;
    const isNotCurrentDriver = driver.accountId !== currentDriverId;
    const isSameDsp = !taskDspId || driver.dspId === taskDspId; // 如果任务有dspId，必须匹配
    
    return isActive && isNotCurrentDriver && isSameDsp && driver.isEmployed;
  });

  // 转单mutation
  const [transferParcels, { loading: transferring }] = useMutation(TRANSFER_PARCELS);

  // 当 Drawer 打开时，重置所有状态，避免脏数据遗留
  useEffect(() => {
    if (visible) {
      // ✅ 重置所有状态到初始值（单司机模式）
      setActiveTab('shipment');
      setSelectedRowKeys([]);
      setSelectedParcels([]);
      setSelectedZipcodes([]);
      setSelectedDriverId(undefined);
      setExpandedZipcodes([]);
      setSubmitting(false);

      // ✅ 关键：每次 Drawer 打开时，强制重新获取最新数据
      // network-only 只是"不用缓存"，但不会自动触发重新查询
      // 必须手动调用 refetch() 才能在 visible 变化时重新请求
      if (taskId) {
        refetch();
      }
    }
  }, [visible, taskId, refetch]);


  // Zipcode分组
  const zipcodeGroups = useMemo(() => {
    return orderDetails.reduce((acc, order) => {
      const zipcode = order.receiverZipcode || 'Unknown';
      if (!acc[zipcode]) {
        acc[zipcode] = {
          zipcode: zipcode,
          count: 0,
          parcels: []
        };
      }
      acc[zipcode].count++;
      acc[zipcode].parcels.push(order);
      return acc;
    }, {} as Record<string, ZipcodeGroup>);
  }, [orderDetails]);

  // ✅ 单司机模式：不需要获取其他司机的选择

  // Zipcode下拉选项
  const zipcodeOptions = useMemo(() => {
    return Object.values(zipcodeGroups).map(group => ({
      value: group.zipcode,
      label: `${group.zipcode} (${group.count})`,
      count: group.count
    }));
  }, [zipcodeGroups]);

  // 切换 zipcode 展开/收起
  const toggleZipcode = (zipcode: string) => {
    setExpandedZipcodes(prev => 
      prev.includes(zipcode) 
        ? prev.filter(z => z !== zipcode)
        : [...prev, zipcode]
    );
  };

  const columns = [
    {
      title: t('delivery.allocateParcels.label.zipcode'),
      dataIndex: 'receiverZipcode',
      key: 'receiverZipcode',
      render: (text: string | null) => <span style={{ color: '#303030' }}>{text || '-'}</span>
    },
    {
      title: t('delivery.allocateParcels.label.stop'),
      dataIndex: 'stopNumber',
      key: 'stopNumber',
      render: (text: number) => <span style={{ color: '#303030' }}>{text}</span>
    },
    {
      title: t('delivery.allocateParcels.label.subStop'),
      dataIndex: 'subStopNumber',
      key: 'subStopNumber',
      render: (text: string) => <span style={{ color: '#303030' }}>{text || '-'}</span>
    },
    {
      title: t('delivery.allocateParcels.label.trackingNo'),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      render: (text: string, record: DeliveryTaskOrderDetail) => (
        <span style={{ color: '#7A33B7' }}>
          {text}
          {record.isPickedUp && (
            <span style={{ marginLeft: 6, fontSize: 11, color: '#fa8c16', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 3, padding: '0 4px' }}>
              {t('delivery.allocateParcels.label.pickedUp')}
            </span>
          )}
        </span>
      ),
    },
  ];

  // ✅ 单司机模式：直接选择包裹，无需检查其他司机
  const rowSelection = {
    selectedRowKeys: selectedParcels,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedParcels(selectedKeys);
    },
  };

  // ✅ 司机选择处理函数（单司机模式）
  const handleDriverChange = (driverId: string) => {
    setSelectedDriverId(driverId);
  };

  // ✅ Zipcode选择处理函数
  const handleZipcodeChange = (selectedZipcodes: string[]) => {
    setSelectedZipcodes(selectedZipcodes);
  };

  // ✅ 计算zipcode模式已选包裹数量
  const getZipcodeParcelCount = () => {
    return selectedZipcodes.reduce(
      (sum, zipcode) => sum + (zipcodeGroups[zipcode]?.count || 0), 
      0
    );
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true); // 开始提交，显示全局loading
      
      // ✅ 单司机模式验证
      if (!selectedDriverId) {
        message.error(t('delivery.allocateParcels.message.pleaseSelectDriver'));
        setSubmitting(false);
        return;
      }
      
      // 根据当前模式获取包裹IDs
      let orderIds: number[] = [];
      
      if (activeTab === 'shipment') {
        // Shipment模式：验证是否选择了包裹
        if (selectedParcels.length === 0) {
          message.error(t('delivery.allocateParcels.message.selectDriverAndParcels'));
          setSubmitting(false);
          return;
        }
        
        // 从选中的 id 中找到对应的订单，并提取 orderId
        orderIds = selectedParcels.map(selectedId => {
          const order = orderDetails.find(o => o.id === String(selectedId));
          return order ? parseInt(order.orderId) : null;
        }).filter(id => id !== null) as number[];
      } else {
        // Zipcode模式：验证是否选择了zipcode
        if (selectedZipcodes.length === 0) {
          message.error(t('delivery.allocateParcels.message.selectDriverAndZipcodes'));
          setSubmitting(false);
          return;
        }
        
        // 从选中的 zipcode 中提取所有订单的 orderId
        orderIds = selectedZipcodes.flatMap(
          zipcode => zipcodeGroups[zipcode]?.parcels.map(p => parseInt(p.orderId)) || []
        );
      }

      if (!taskId) {
        message.error('Task ID is missing');
        setSubmitting(false);
        return;
      }

      // ✅ 检查包裹数量是否超过 500 个（请求前拦截）
      if (orderIds.length > 1000) {
        message.error(`选择的包裹数量不能超过 1000 个，当前已选择 ${orderIds.length} 个`);
        setSubmitting(false);
        return;
      }

      try {
        const input: TransferParcelsInput = {
          sourceTaskId: parseInt(taskId),
          driverId: parseInt(selectedDriverId!),
          // 使用前端本地当天日期作为任务日期（YYYY-MM-DD）
          taskDate: dayjs().format('YYYY-MM-DD'),
          parcelIds: orderIds
        };

        const { data: result } = await transferParcels({
          variables: { input }
        });

        // 检查后端返回的结果
        if (!result?.transferParcels || result.transferParcels.status === 'FAILED') {
          const errorCode = result?.transferParcels?.errorCode;
          const errorCodeToI18nKey: Record<string, string> = {
            PARCELS_IN_DELIVERED_STATUS: 'transferFailedParcelsInDeliveredStatus',
            SOURCE_TASK_NOT_FOUND: 'transferFailedSourceTaskNotFound',
            SOURCE_TASK_COMPLETED: 'transferFailedSourceTaskCompleted',
            EMPTY_PARCEL_LIST: 'transferFailedEmptyParcelList',
            TRANSFER_START_FAILED: 'transferFailedStartError',
          };
          const i18nKey = errorCode && errorCodeToI18nKey[errorCode]
            ? `delivery.allocateParcels.message.${errorCodeToI18nKey[errorCode]}`
            : 'delivery.allocateParcels.message.transferFailed';
          const errorContent = t(i18nKey, {
            trackingNumbers: result?.transferParcels?.failedTrackingNumbers?.join(', '),
          });
          message.error({
            content: errorContent,
            duration: 6,
            style: {
              marginTop: '20vh',
            }
          });
          setSubmitting(false);
          return;
        }

        // ✅ 转单请求已提交，获取 taskId（不在 Drawer 内轮询，直接返回父组件）
        const transferTaskId = result.transferParcels.taskId;
        
        console.log(`🚀 Transfer task ${transferTaskId} started, returning to task page...`);
        
        // 显示提交成功提示
        message.success({
          content: t('delivery.allocateParcels.message.transferSubmitted'),
          duration: 2
        });

        // 立即关闭 Drawer 并返回父组件，传递 transferTaskId
        setSubmitting(false);
        onClose();
        onSuccess?.(transferTaskId); // 传递 taskId 给父组件开始轮询
      } catch (innerError: any) {
        console.error('Transfer error:', innerError);
        // 处理 GraphQL 或网络错误
        const errorMessage = innerError.message || t('delivery.allocateParcels.message.transferError');
        message.error({
          content: errorMessage,
          duration: 6,
          style: {
            marginTop: '20vh',
          }
        });
        setSubmitting(false);
      }
    } catch (error: any) {
      console.error('Transfer error:', error);
      // 处理意外错误
      const errorMessage = error.message || t('delivery.allocateParcels.message.transferError');
      message.error({
        content: errorMessage,
        duration: 6,
        style: {
          marginTop: '20vh',
        }
      });
      setSubmitting(false);
    }
  };

  const segmentedOptions = [
    { label: t('delivery.allocateParcels.label.shipment'), value: 'shipment' },
    { label: t('delivery.allocateParcels.label.zipcode'), value: 'zipcode' },
  ];

  const renderContent = () => {
    // ✅ 单司机模式：直接显示可选择的表格，不需要"选择中"状态
    
    // Shipment模式：按stopNumber排序
    const sortedByStopNumber = [...orderDetails]
      .sort((a, b) => (a.stopNumber ?? 0) - (b.stopNumber ?? 0))
      .map(p => ({ ...p, key: p.id }));

    if (activeTab === 'shipment') {
      return (
        <div className="h-full flex flex-col px-7 py-6 pr-16 shadow-md rounded-lg bg-white box-border">
          <div className="mb-4 flex-shrink-0">
            <span className="text-lg font-semibold">
              {t('delivery.allocateParcels.label.allocatableParcels')}: 
              <span className="font-bold ml-2">{orderDetails.length}</span>
              {selectedParcels.length > 0 && (
                <span className="ml-4 text-blue-600">
                  ({t('delivery.allocateParcels.label.selected')}: {selectedParcels.length})
                </span>
              )}
            </span>
          </div>
          <div className="flex-1 overflow-auto">
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={sortedByStopNumber}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                size: 'small',
                showTotal: (total) => t('table.pagination.totalItems', { total })
              }}
              tableLayout="auto"
              className="font-semibold text-gray-800"
            />
          </div>
        </div>
      );
    } else if (activeTab === 'zipcode') {
      return (
        <div className="h-full flex flex-col px-7 py-6 pr-16 shadow-md rounded-lg bg-white box-border">
          <div className="mb-4 flex-shrink-0">
            <span className="text-lg font-semibold">{t('delivery.allocateParcels.label.zipcode')}: <span className="font-bold">{Object.keys(zipcodeGroups).length}</span></span>
            <span className="text-lg font-semibold ml-4">{t('delivery.allocateParcels.label.allocatableParcels')}: <span className="font-bold">{orderDetails.length}</span></span>
          </div>
          <div className="flex-1 overflow-auto">
            {Object.values(zipcodeGroups).map((group) => (
              <div key={group.zipcode} className="mb-4">
                <div 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleZipcode(group.zipcode)}
                >
                  <span className="font-medium">{group.zipcode} ({group.count})</span>
                  <DownOutlined 
                    className={`transition-transform ${expandedZipcodes.includes(group.zipcode) ? 'rotate-180' : ''}`}
                  />
                </div>
                {expandedZipcodes.includes(group.zipcode) && (
                  <div className="mt-2">
                    <Table
                      columns={columns}
                      dataSource={group.parcels.map(p => ({ ...p, key: p.id }))}
                      pagination={{
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        size: 'small',
                        showTotal: (total) => t('table.pagination.totalItems', { total })
                      }}
                      tableLayout="auto"
                      className="font-semibold text-gray-800"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  return (
    <Drawer
      title={
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-2xl font-bold text-black">{t('delivery.allocateParcels.title')}</span>
          </div>
        </div>
      }
      width={window.innerWidth * 0.85}
      placement="right"
      closable={false}
      onClose={onClose}
      open={visible}
      styles={{ 
        header: { borderBottom: 'none', padding: '30px 50px', height: 'auto' },
        body: { padding: '0 50px 0'}
      }}
    >
      <Spin 
        spinning={submitting || orderDetailsLoading} 
        tip={
          orderDetailsLoading 
            ? t('delivery.allocateParcels.message.loadingOrderDetails')
            : t('delivery.allocateParcels.message.processing')
        }
        size="large"
        indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
      >
        <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 100px)' }}>
          {/* 分段控制器盒子 - 固定在顶部 */}
          <div className="bg-white rounded mb-4 w-1/2 flex-shrink-0">
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  itemSelectedBg: '#1170FF',
                  itemSelectedColor: '#FFFFFF',
                  itemColor: '#7E7E7E',
                  trackBg: '#F7F7F7',
                },
              },
            }}
          >
            <Segmented
              options={segmentedOptions}
              value={activeTab}
              onChange={(value) => setActiveTab(value as 'shipment' | 'zipcode')}
              size="large"
              shape="round"
              block
            />
          </ConfigProvider>
        </div>

        {/* 主内容盒子 - 占据剩余空间 */}
        <div className="flex gap-6 flex-1 min-h-0">
          {/* 左侧：列表区域 */}
          <div className="flex-1 w-1/2">
            {renderContent()}
          </div>

          {/* 右侧：司机分配操作区 */}
          <div className="flex w-1/2 flex-col">
            <div className="flex-1 overflow-auto">
              {/* ✅ 单司机模式：直接渲染单个司机选择器 */}
              {activeTab === 'shipment' ? (
                // ✅ Shipment模式 - 单司机
                <div className="mb-4 px-12 py-6 rounded-lg bg-white box-border shadow-md border border-gray-200">
                  <div className="mb-4">
                    <div className="flex items-center">
                      <span className="text-base font-medium mr-3" style={{ color: 'rgba(48, 48, 48, 1)' }}>{t('delivery.allocateParcels.label.driverToAssign')}</span>
                      <Select
                        placeholder={t('delivery.allocateParcels.placeholder.pleaseSelect')}
                        style={{ width: '360px' }}
                        value={selectedDriverId}
                        onChange={(value) => handleDriverChange(value as string)}
                        showSearch
                        optionFilterProp="dataLabel"
                        filterOption={(input, option) =>
                          (option?.dataLabel ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={activeDrivers.map(driver => {
                          const isVerified = driver.verificationStatus == 2;
                          const isBlacklisted = !!driver.isBlacklist;
                          const labelText = `${driver.name} (${driver.phone})`;
                          return {
                            value: driver.accountId.toString(),
                            dataLabel: labelText,
                            disabled: isBlacklisted || (isDriverVerificationEnabled && !isVerified),
                            label: (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{labelText}</span>
                                {isBlacklisted ? (
                                  <span style={{ fontSize: 12, color: '#faad14' }}>
                                    {t('delivery.routes.label.driverBlacklistedTag')}
                                  </span>
                                ) : isDriverVerificationEnabled && isVerified ? (
                                  <span style={{ fontSize: 12, color: '#52c41a' }}>
                                    {t('delivery.routes.label.driverVerified')}
                                  </span>
                                ) : isDriverVerificationEnabled && !isVerified ? (
                                  <span style={{ fontSize: 12, color: '#faad14' }}>
                                    {t('delivery.routes.label.driverUnverifiedShort')}
                                  </span>
                                ) : null}
                              </span>
                            )
                          };
                        })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center mb-3">
                    <span className="mr-2" style={{ color: 'rgba(48, 48, 48, 1)' }}>{t('delivery.allocateParcels.label.selected')}</span>
                    <LockOutlined className="mr-1" style={{ color: 'rgba(48, 48, 48, 1)' }} />
                    <span className="font-semibold mr-2" style={{ color: 'rgba(48, 48, 48, 1)' }}>{selectedParcels.length}</span>
                    <span className="text-sm text-gray-500">
                      {selectedParcels.length > 0 && `(${t('delivery.allocateParcels.label.parcels')})`}
                    </span>
                  </div>

                </div>
              ) : (
                // ✅ Zipcode模式 - 单司机
                <div className="mb-4 px-12 py-6 rounded-lg bg-white box-border shadow-md border border-gray-200">
                  <div className="mb-4">
                    <div className="flex items-center">
                      <span className="text-base font-medium mr-3" style={{ color: 'rgba(48, 48, 48, 1)' }}>{t('delivery.allocateParcels.label.driverToAssign')}</span>
                      <Select
                        placeholder={t('delivery.allocateParcels.placeholder.pleaseSelect')}
                        style={{ width: '360px' }}
                        value={selectedDriverId}
                        onChange={(value) => handleDriverChange(value as string)}
                        showSearch
                        optionFilterProp="dataLabel"
                        filterOption={(input, option) =>
                          (option?.dataLabel ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={activeDrivers.map(driver => {
                          const isVerified = driver.verificationStatus == 2;
                          const isBlacklisted = !!driver.isBlacklist;
                          const labelText = `${driver.name} (${driver.phone})`;
                          return {
                            value: driver.accountId.toString(),
                            dataLabel: labelText,
                            disabled: isBlacklisted || (isDriverVerificationEnabled && !isVerified),
                            label: (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{labelText}</span>
                                {isBlacklisted ? (
                                  <span style={{ fontSize: 12, color: '#ff4d4f' }}>
                                    {t('delivery.routes.label.driverBlacklistedTag')}
                                  </span>
                                ) : isDriverVerificationEnabled && isVerified ? (
                                  <span style={{ fontSize: 12, color: '#52c41a' }}>
                                    {t('delivery.routes.label.driverVerified')}
                                  </span>
                                ) : isDriverVerificationEnabled && !isVerified ? (
                                  <span style={{ fontSize: 12, color: '#faad14' }}>
                                    {t('delivery.routes.label.driverUnverifiedShort')}
                                  </span>
                                ) : null}
                              </span>
                            )
                          };
                        })}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 flex items-center">
                      <span className="text-base font-medium mr-3" style={{ color: 'rgba(48, 48, 48, 1)' }}>{t('delivery.allocateParcels.label.zipcode')}</span>
                      <Select
                        mode="multiple"
                        placeholder={t('delivery.allocateParcels.placeholder.pleaseSelectZipcode')}
                        style={{ flex: 1 }}
                        value={selectedZipcodes}
                        onChange={handleZipcodeChange}
                        options={zipcodeOptions}
                      />
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2" style={{ color: 'rgba(48, 48, 48, 1)' }}>{t('delivery.allocateParcels.label.selected')}</span>
                      <LockOutlined className="mr-1" style={{ color: 'rgba(48, 48, 48, 1)' }} />
                      <span className="font-semibold mr-2" style={{ color: 'rgba(48, 48, 48, 1)' }}>{selectedZipcodes.length}</span>
                      <span className="text-sm text-gray-500">({getZipcodeParcelCount()} {t('delivery.allocateParcels.label.parcels')})</span>
                    </div>

                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 底部盒子 - 固定在底部 */}
        <div className="bg-white p-6 rounded mt-4 flex-shrink-0">
          <div className="flex justify-end gap-4">
            <Button size="large" onClick={onClose} style={{ width: '160px' }}>
              {t('delivery.allocateParcels.label.cancel').toUpperCase()}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleConfirm}
              loading={submitting || transferring}
              disabled={submitting}
              style={{ width: '240px', backgroundColor: '#7A33B7' }}
            >
              {t('delivery.allocateParcels.label.confirmAllocation')}
            </Button>
          </div>
        </div>
        </div>
      </Spin>
    </Drawer>
  );
};

export default AllocateParcelsDrawer;
